import React from "react";
import { StatCard } from "../../../components/StatCard";
import AppointmentsTableCard from "../../../components/Files/AppointmentsTableCard";
import {
  Clock,
  Loader2,
  PauseCircle,
  CheckCircle2,
  XCircle,
  RotateCw,
  AlertCircle,
  FileText,
  ExternalLink,
  Layers,
  Package,
  Boxes,
} from "lucide-react";

// Props type
interface OrdersTabProps {
  activeTab: string;
  productTotal: any[]; // replace with proper type
  orderTotal: any[]; // replace with proper type
  coupons: any[]; // replace with proper type
}

interface OrderCard {
  title: string;
  value?: number;
  icon: React.ReactElement; // ✅ must be ReactElement
  gradient: string;
}

// Get icon for order status
const getIconForStatus = (slug: string): React.ReactElement => {
  switch (slug) {
    case "pending":
      return <Clock className="text-white" />;
    case "processing":
      return <Loader2 className="text-white animate-spin" />;
    case "on-hold":
      return <PauseCircle className="text-white" />;
    case "completed":
      return <CheckCircle2 className="text-white" />;
    case "cancelled":
      return <XCircle className="text-white" />;
    case "refunded":
      return <RotateCw className="text-white" />;
    case "failed":
      return <AlertCircle className="text-white" />;
    case "checkout-draft":
      return <FileText className="text-white" />;
    case "external":
      return <ExternalLink className="text-white" />;
    case "grouped":
      return <Layers className="text-white" />;
    case "simple":
      return <Package className="text-white" />;
    case "variable":
      return <Boxes className="text-white" />;
    default:
      return <FileText className="text-white" />;
  }
};

// Get gradient for order status
const getGradientForStatus = (slug: string): string => {
  switch (slug) {
    case "pending":
      return "from-yellow-400 to-yellow-600";
    case "processing":
      return "from-blue-400 to-blue-600";
    case "on-hold":
      return "from-orange-400 to-orange-600";
    case "completed":
      return "from-green-400 to-green-600";
    case "cancelled":
      return "from-red-400 to-red-600";
    case "refunded":
      return "from-purple-400 to-purple-600";
    case "failed":
      return "from-red-700 to-red-900";
    case "checkout-draft":
      return "from-gray-400 to-gray-600";
    case "external":
      return "from-indigo-400 to-indigo-600";
    case "grouped":
      return "from-teal-400 to-teal-600";
    case "simple":
      return "from-cyan-400 to-cyan-600";
    case "variable":
      return "from-pink-400 to-pink-600";
    default:
      return "from-gray-400 to-gray-600";
  }
};

// Convert order data to StatCard format
const getOrderTotalCards = (data: any[]): OrderCard[] => {
  return data.map((item) => ({
    title: item.name,
    value: item.total,
    icon: getIconForStatus(item.slug),
    gradient: getGradientForStatus(item.slug),
  }));
};

const OrdersTab: React.FC<OrdersTabProps> = ({
  activeTab,
  productTotal,
  orderTotal,
  coupons,
}) => {

  const cards = [
    ...getOrderTotalCards(productTotal),
    ...getOrderTotalCards(orderTotal),
  ].filter((card) => card.title !== "On hold" && card.title !== "Draft");

  return (
    <>
      <h3 className="text-2xl font-semibold text-gray-800 mb-4 border-b-4 border-green-500 pb-2">
        Orders
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-10">
        {cards.map((card, index) => (
          <StatCard
            key={index}
            title={card.title}
            value={card.value || 0}
            icon={card.icon}
            bgGradient={card.gradient}
          />
        ))}
      </div>

      <div className="gap-6 mt-5">
        <AppointmentsTableCard
          title="Active Coupons"
          data={coupons}
          navigatePath="/coupons"
          columns={[
            {
              key: "code",
              label: "Code",
              render: (item) => (
                <span className="text-blue-600 truncate max-w-[100px]">{item.code}</span>
              ),
            },
            {
              key: "amount",
              label: "Value",
              render: (item) =>
                item.discount_type === "percent"
                  ? `${item.amount}%`
                  : `₹ ${item.amount}`,
            },
            {
              key: "status",
              label: "Status",
              render: (item) => item.status || "Unknown",
              isStatus: true,
            },
            {
              key: "discount_type",
              label: "Discount Type",
              render: (item) => <span className="capitalize">{item.discount_type}</span>,
            },
            {
              key: "date_expires",
              label: "Valid Till",
              render: (item) =>
                new Date(item.date_expires).toLocaleDateString("en-GB"),
            },
            { key: "usage_count", label: "Usage Count" },
          ]}
          statusColorMap={{
            publish: "bg-green-200 text-green-900",
            active: "bg-green-200 text-green-900",
            pending: "bg-yellow-200 text-yellow-900",
            draft: "bg-gray-200 text-gray-900",
            future: "bg-blue-200 text-blue-900",
            private: "bg-purple-200 text-purple-900",
            trash: "bg-red-200 text-red-900",
            expired: "bg-red-200 text-red-900",
            used: "bg-gray-200 text-gray-900",
            inactive: "bg-yellow-200 text-yellow-900",
          }}
        />
      </div>
    </>
  );
};

export default OrdersTab;
